/**
 * LeftShield Admin - Identity Page (Platform Users)
 * Manage platform administrators and their permissions
 */
import React, { useState } from 'react';
import {
  Title1,
  Body1,
  Body2,
  Card,
  CardHeader,
  Button,
  Badge,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Spinner,
  tokens,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Input,
  Field,
  Dropdown,
  Option,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Tooltip,
} from '@fluentui/react-components';
import {
  PeopleRegular,
  AddRegular,
  PersonRegular,
  MailRegular,
  CalendarRegular,
  MoreVerticalRegular,
  ShieldCheckmarkRegular,
  PersonProhibitedRegular,
  ArrowSyncRegular,
  KeyResetRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { identityApi } from '../services/api';
import type { PlatformUser, PlatformUserRole, PlatformUserStatus, InvitePlatformUserRequest } from '../types';

const roleColors: Record<PlatformUserRole, 'danger' | 'warning' | 'success' | 'informative'> = {
  SuperAdmin: 'danger',
  PlatformAdmin: 'warning',
  Support: 'success',
  ReadOnly: 'informative',
};

const statusColors: Record<PlatformUserStatus, 'success' | 'warning' | 'subtle'> = {
  Active: 'success',
  Invited: 'warning',
  Disabled: 'subtle',
};

const roleDescriptions: Record<PlatformUserRole, string> = {
  SuperAdmin: 'Full platform access including settings and user management',
  PlatformAdmin: 'Manage tenants, trials, and view all data',
  Support: 'View and assist with tenant issues',
  ReadOnly: 'View-only access to platform data',
};

export const IdentityPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState<InvitePlatformUserRequest>({
    email: '',
    displayName: '',
    role: 'Support',
  });

  const {
    data: users,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['platformUsers'],
    queryFn: () => identityApi.getAll(),
  });

  const inviteMutation = useMutation({
    mutationFn: (request: InvitePlatformUserRequest) => identityApi.invite(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platformUsers'] });
      setIsInviteDialogOpen(false);
      setInviteForm({ email: '', displayName: '', role: 'Support' });
    },
  });

  const disableMutation = useMutation({
    mutationFn: (userId: string) => identityApi.disable(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platformUsers'] }),
  });

  const enableMutation = useMutation({
    mutationFn: (userId: string) => identityApi.enable(userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platformUsers'] }),
  });

  const resendInviteMutation = useMutation({
    mutationFn: (userId: string) => identityApi.resendInvitation(userId),
  });

  const resetMfaMutation = useMutation({
    mutationFn: (userId: string) => identityApi.resetMfa(userId),
  });

  const handleInvite = () => {
    if (inviteForm.email && inviteForm.displayName) {
      inviteMutation.mutate(inviteForm);
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Spinner size="large" label="Loading platform users..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card style={{ margin: '1rem', padding: '1rem' }}>
        <Body1 style={{ color: tokens.colorPaletteRedForeground1 }}>
          Error loading users: {error instanceof Error ? error.message : 'Unknown error'}
        </Body1>
      </Card>
    );
  }

  const activeUsers = users?.filter(u => u.status === 'Active').length ?? 0;
  const invitedUsers = users?.filter(u => u.status === 'Invited').length ?? 0;

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <PeopleRegular style={{ fontSize: '2rem' }} />
          <div>
            <Title1>Platform Users</Title1>
            <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
              {activeUsers} active, {invitedUsers} pending
            </Body2>
          </div>
        </div>
        <Button
          appearance="primary"
          icon={<AddRegular />}
          onClick={() => setIsInviteDialogOpen(true)}
        >
          Invite User
        </Button>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader
          header={<Body1 style={{ fontWeight: 600 }}>All Platform Users ({users?.length ?? 0})</Body1>}
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>User</TableHeaderCell>
              <TableHeaderCell>Role</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>MFA</TableHeaderCell>
              <TableHeaderCell>Last Login</TableHeaderCell>
              <TableHeaderCell>Created</TableHeaderCell>
              <TableHeaderCell style={{ width: '50px' }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users?.map((user: PlatformUser) => (
              <TableRow key={user.id}>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <PersonRegular />
                    <div>
                      <div style={{ fontWeight: 500 }}>{user.displayName}</div>
                      <div style={{ fontSize: '0.875rem', color: tokens.colorNeutralForeground3 }}>
                        {user.email}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Tooltip content={roleDescriptions[user.role]} relationship="label">
                    <Badge appearance="filled" color={roleColors[user.role]}>
                      {user.role}
                    </Badge>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={statusColors[user.status]}>
                    {user.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {user.mfaEnabled ? (
                    <Badge appearance="tint" color="success" icon={<ShieldCheckmarkRegular />}>
                      Enabled
                    </Badge>
                  ) : (
                    <Badge appearance="tint" color="subtle">
                      Disabled
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {user.lastLoginAt ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <CalendarRegular />
                      {new Date(user.lastLoginAt).toLocaleDateString()}
                    </div>
                  ) : (
                    <span style={{ color: tokens.colorNeutralForeground3 }}>Never</span>
                  )}
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <CalendarRegular />
                    {new Date(user.createdAt).toLocaleDateString()}
                  </div>
                </TableCell>
                <TableCell>
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button
                        appearance="subtle"
                        icon={<MoreVerticalRegular />}
                        size="small"
                      />
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        {user.status === 'Invited' && (
                          <MenuItem
                            icon={<MailRegular />}
                            onClick={() => resendInviteMutation.mutate(user.id)}
                          >
                            Resend Invitation
                          </MenuItem>
                        )}
                        {user.status === 'Active' && (
                          <>
                            <MenuItem
                              icon={<KeyResetRegular />}
                              onClick={() => resetMfaMutation.mutate(user.id)}
                            >
                              Reset MFA
                            </MenuItem>
                            <MenuItem
                              icon={<PersonProhibitedRegular />}
                              onClick={() => disableMutation.mutate(user.id)}
                            >
                              Disable User
                            </MenuItem>
                          </>
                        )}
                        {user.status === 'Disabled' && (
                          <MenuItem
                            icon={<ArrowSyncRegular />}
                            onClick={() => enableMutation.mutate(user.id)}
                          >
                            Enable User
                          </MenuItem>
                        )}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
            {(!users || users.length === 0) && (
              <TableRow>
                <TableCell colSpan={7}>
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Body1>No platform users found</Body1>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Invite User Dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={(_, data) => setIsInviteDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Invite Platform User</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingTop: '1rem' }}>
                <Field label="Email Address" required>
                  <Input
                    type="email"
                    value={inviteForm.email}
                    onChange={(_, data) => setInviteForm({ ...inviteForm, email: data.value })}
                    placeholder="user@company.com"
                    contentBefore={<MailRegular />}
                  />
                </Field>
                <Field label="Display Name" required>
                  <Input
                    value={inviteForm.displayName}
                    onChange={(_, data) => setInviteForm({ ...inviteForm, displayName: data.value })}
                    placeholder="John Smith"
                    contentBefore={<PersonRegular />}
                  />
                </Field>
                <Field label="Role" required>
                  <Dropdown
                    value={inviteForm.role}
                    selectedOptions={[inviteForm.role]}
                    onOptionSelect={(_, data) => setInviteForm({ ...inviteForm, role: data.optionValue as PlatformUserRole })}
                  >
                    <Option value="SuperAdmin" text="Super Admin">
                      <div>
                        <div style={{ fontWeight: 500 }}>Super Admin</div>
                        <div style={{ fontSize: '0.75rem', color: tokens.colorNeutralForeground3 }}>
                          {roleDescriptions.SuperAdmin}
                        </div>
                      </div>
                    </Option>
                    <Option value="PlatformAdmin" text="Platform Admin">
                      <div>
                        <div style={{ fontWeight: 500 }}>Platform Admin</div>
                        <div style={{ fontSize: '0.75rem', color: tokens.colorNeutralForeground3 }}>
                          {roleDescriptions.PlatformAdmin}
                        </div>
                      </div>
                    </Option>
                    <Option value="Support" text="Support">
                      <div>
                        <div style={{ fontWeight: 500 }}>Support</div>
                        <div style={{ fontSize: '0.75rem', color: tokens.colorNeutralForeground3 }}>
                          {roleDescriptions.Support}
                        </div>
                      </div>
                    </Option>
                    <Option value="ReadOnly" text="Read Only">
                      <div>
                        <div style={{ fontWeight: 500 }}>Read Only</div>
                        <div style={{ fontSize: '0.75rem', color: tokens.colorNeutralForeground3 }}>
                          {roleDescriptions.ReadOnly}
                        </div>
                      </div>
                    </Option>
                  </Dropdown>
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setIsInviteDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleInvite}
                disabled={!inviteForm.email || !inviteForm.displayName || inviteMutation.isPending}
              >
                {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
